/**
 * Violations & Events — live compliance event stream with pattern detection.
 *
 * Two complementary views:
 *   1. Patterns tab (default) — detected violation PATTERNS (repeated blocks,
 *      escalating injection attempts, account lockouts, anomalies). These are
 *      high-signal aggregated findings an auditor/IT admin acts on.
 *   2. Blocked Events tab — raw event stream filtered to blocked/failed
 *      outcomes and warning+ severities. Every row is one underlying event.
 *
 * Pattern drill-in: clicking a pattern opens a sheet showing all related
 * events in sequence + recommended action + status change workflow.
 */

import { createFileRoute, Link } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import {
  Shield, ShieldAlert, AlertTriangle, Activity, Search, Download,
  ChevronRight, User, Bot, Clock, CheckCircle2, XCircle, ExternalLink,
  Filter, Lock, Zap, Brain, Flame,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { SortableHeader, useTableSort } from '@/components/ui/data-table';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  SEVERITY_STYLES, OUTCOME_STYLES, CATEGORY_STYLES,
  type ViolationPattern, type ActivityEvent, type Severity,
} from '@/lib/mock-trust-activity';
import { useTrustActivity, useTrustViolations } from '@/lib/use-trust-module';
import { apiClient } from '@/lib/api-client';
import { useTrustEntities, shortId } from '@/lib/use-trust-entities';
import { ActivityDetailSheet } from '@/components/trust/activity-detail-sheet';
import { triggerDownload, generateCsv } from '@/lib/trust-downloads';

export const Route = createFileRoute('/trust/violations')({
  component: ViolationsPage,
});

type ViolationTab = 'patterns' | 'events';
type DateRange = '24h' | '7d' | '30d' | 'all';

// Neutral style used when a real event/pattern carries a category/outcome/
// severity/status value that isn't in the curated style maps. Reading `.color`
// on an undefined map hit would crash the tab via the error boundary; this
// renders the raw value neutrally instead. Mirrors activity.tsx's FALLBACK_STYLE.
const TRUST_FALLBACK_STYLE = { label: '', color: 'text-muted-foreground border-muted-foreground/30' } as const;

// ─── Pattern type icons ─────────────────────────────────────────────────────

const PATTERN_TYPE_META: Record<ViolationPattern['patternType'], { label: string; icon: typeof Shield; color: string }> = {
  single_event:   { label: 'Single event',       icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400' },
  repeated:       { label: 'Repeated pattern',   icon: Flame,         color: 'text-orange-600 dark:text-orange-400' },
  escalating:     { label: 'Escalating',         icon: Flame,         color: 'text-red-600 dark:text-red-400' },
  anomaly:        { label: 'Anomaly',            icon: Brain,         color: 'text-violet-600 dark:text-violet-400' },
  known_attack:   { label: 'Known attack',       icon: ShieldAlert,   color: 'text-red-600 dark:text-red-400' },
};

// A pattern is a server-resolvable incident only when its id is a real
// governance_incidents UUID; derived/demo patterns carry non-UUID ids.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const PATTERN_STATUS_STYLES: Record<ViolationPattern['status'], { label: string; color: string }> = {
  open:            { label: 'Open',             color: 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20' },
  investigating:   { label: 'Investigating',    color: 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20' },
  resolved:        { label: 'Resolved',         color: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20' },
  false_positive:  { label: 'False positive',   color: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400 border-zinc-500/20' },
};

function ViolationsPage() {
  // Real-data: governance_incidents + derived audit_logs failures
  const { data: realViolations = [] } = useTrustViolations(200);
  const { data: realActivityEvents = [] } = useTrustActivity(1000);
  const VIOLATION_PATTERNS = realViolations;
  const ACTIVITY_EVENTS = realActivityEvents;
  void VIOLATION_PATTERNS; void ACTIVITY_EVENTS;
  const entities = useTrustEntities();
  const [tab, setTab] = useState<ViolationTab>('patterns');
  const [search, setSearch] = useState('');
  const [severityFilter, setSeverityFilter] = useState<'all' | Severity>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | ViolationPattern['status']>('all');
  const [dateRange, setDateRange] = useState<DateRange>('7d');
  const [selectedPattern, setSelectedPattern] = useState<ViolationPattern | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(null);

  // ── Patterns filter ──────────────────────────────────────────────────────
  const filteredPatterns = useMemo(() => {
    return VIOLATION_PATTERNS.filter(p => {
      if (severityFilter !== 'all' && p.severity !== severityFilter) return false;
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      if (!search) return true;
      const q = search.toLowerCase();
      return (
        p.title.toLowerCase().includes(q)
        || p.description.toLowerCase().includes(q)
        || (p.affectedUserId?.toLowerCase().includes(q) ?? false)
        || (p.affectedAgentId?.toLowerCase().includes(q) ?? false)
      );
    });
  }, [VIOLATION_PATTERNS, search, severityFilter, statusFilter]);

  // ── Blocked / warning events filter ──────────────────────────────────────
  const blockedEvents = useMemo(() => {
    const now = Date.now();
    const cutoffMs =
      dateRange === '24h' ? 86_400_000 :
      dateRange === '7d'  ? 604_800_000 :
      dateRange === '30d' ? 2_592_000_000 :
      Infinity;
    const cutoff = now - cutoffMs;
    return ACTIVITY_EVENTS.filter(e =>
      new Date(e.timestamp).getTime() >= cutoff
      && (e.outcome.startsWith('blocked_')
        || e.outcome === 'failed_error'
        || e.outcome === 'failed_timeout'
        || e.severity === 'warning'
        || e.severity === 'error'
        || e.severity === 'critical'
      )
      && (severityFilter === 'all' || e.severity === severityFilter)
      && (!search || (
        e.summary.toLowerCase().includes(search.toLowerCase())
        || (e.userId?.toLowerCase().includes(search.toLowerCase()) ?? false)
        || (e.agentId?.toLowerCase().includes(search.toLowerCase()) ?? false)
      )),
    );
  }, [ACTIVITY_EVENTS, search, severityFilter, dateRange]);

  const { sorted: sortedEvents, sortConfig, handleSort } = useTableSort(blockedEvents, 'timestamp', 'desc');

  // ── Stats ────────────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    totalPatterns: VIOLATION_PATTERNS.length,
    openPatterns: VIOLATION_PATTERNS.filter(p => p.status === 'open').length,
    criticalPatterns: VIOLATION_PATTERNS.filter(p => p.severity === 'critical' && p.status !== 'resolved' && p.status !== 'false_positive').length,
    blockedEvents24h: ACTIVITY_EVENTS.filter(e =>
      new Date(e.timestamp).getTime() > Date.now() - 86_400_000
      && e.outcome.startsWith('blocked_'),
    ).length,
  }), []);

  // ── Exports ──────────────────────────────────────────────────────────────
  const handleExportPatterns = () => {
    const csv = generateCsv(
      ['Pattern', 'Severity', 'Type', 'Status', 'Detected At', 'Related Events', 'Affected User', 'Affected Agent', 'Frameworks', 'Recommended Action'],
      filteredPatterns.map(p => [
        p.title, p.severity, p.patternType, p.status, p.detectedAt,
        p.relatedEventIds.length, p.affectedUserId ?? '', p.affectedAgentId ?? '',
        p.frameworkControls.join(' | '), p.recommendedAction,
      ]),
    );
    triggerDownload({
      filename: `violations-patterns-${new Date().toISOString().split('T')[0]}.csv`,
      content: csv,
      mimeType: 'text/csv',
    });
    toast.success(`Exported ${filteredPatterns.length} patterns`);
  };

  const handleExportBlockedEvents = () => {
    const csv = generateCsv(
      ['Seq', 'Timestamp', 'Summary', 'Category', 'Outcome', 'Severity', 'User', 'Agent', 'Classification', 'Frameworks'],
      sortedEvents.map(e => [
        e.sequenceNumber, e.timestamp, e.summary, e.category, e.outcome, e.severity,
        e.userId ?? '', e.agentId ?? '',
        e.classifications.join(' | '),
        e.frameworkControls.join(' | '),
      ]),
    );
    triggerDownload({
      filename: `violations-events-${new Date().toISOString().split('T')[0]}.csv`,
      content: csv,
      mimeType: 'text/csv',
    });
    toast.success(`Exported ${sortedEvents.length} events`);
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ShieldAlert className="h-6 w-6 text-primary" />
            Violations & Events
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every policy block, injection attempt, classification denial, hallucination flag, and compliance anomaly.
            Patterns tab aggregates repeated/escalating signals into investigable findings.
          </p>
        </div>
        <Button
          size="sm" variant="outline" className="gap-1.5"
          onClick={tab === 'patterns' ? handleExportPatterns : handleExportBlockedEvents}
        >
          <Download className="h-3.5 w-3.5" />
          Export {tab === 'patterns' ? `(${filteredPatterns.length})` : `(${sortedEvents.length})`}
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Detected Patterns</span>
              <Shield className="h-3.5 w-3.5 text-primary" />
            </div>
            <p className="text-2xl font-bold">{stats.totalPatterns}</p>
            <p className="text-[10px] text-muted-foreground">{stats.openPatterns} open · rest resolved</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Critical Open</span>
              <Flame className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
            </div>
            <p className={cn('text-2xl font-bold', stats.criticalPatterns > 0 && 'text-red-600 dark:text-red-400')}>
              {stats.criticalPatterns}
            </p>
            <p className="text-[10px] text-muted-foreground">require immediate action</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Blocked (24h)</span>
              <Lock className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-2xl font-bold">{stats.blockedEvents24h}</p>
            <p className="text-[10px] text-muted-foreground">all block types</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-muted-foreground">Total Events (filter)</span>
              <Activity className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />
            </div>
            <p className="text-2xl font-bold">{blockedEvents.length}</p>
            <p className="text-[10px] text-muted-foreground">warnings + blocks + failures</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b">
        <button
          onClick={() => setTab('patterns')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 transition-colors',
            tab === 'patterns' ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground',
          )}
        >
          <Shield className="h-3.5 w-3.5" />
          Patterns
          <span className="text-[9px] text-muted-foreground ml-0.5">({filteredPatterns.length})</span>
        </button>
        <button
          onClick={() => setTab('events')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2 text-xs border-b-2 transition-colors',
            tab === 'events' ? 'border-primary text-primary font-semibold' : 'border-transparent text-muted-foreground',
          )}
        >
          <Activity className="h-3.5 w-3.5" />
          Blocked Events
          <span className="text-[9px] text-muted-foreground ml-0.5">({blockedEvents.length})</span>
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={tab === 'patterns' ? 'Search patterns, users, agents...' : 'Search events...'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 h-9 text-xs"
          />
        </div>

        <Select value={severityFilter} onValueChange={v => setSeverityFilter(v as typeof severityFilter)}>
          <SelectTrigger className="w-32 h-9 text-xs"><SelectValue placeholder="Severity" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All severities</SelectItem>
            {(Object.keys(SEVERITY_STYLES) as Severity[]).map(s => (
              <SelectItem key={s} value={s} className="text-xs">{SEVERITY_STYLES[s].label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {tab === 'patterns' ? (
          <Select value={statusFilter} onValueChange={v => setStatusFilter(v as typeof statusFilter)}>
            <SelectTrigger className="w-36 h-9 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="investigating">Investigating</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="false_positive">False positive</SelectItem>
            </SelectContent>
          </Select>
        ) : (
          <Select value={dateRange} onValueChange={v => setDateRange(v as DateRange)}>
            <SelectTrigger className="w-28 h-9 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="24h">Last 24h</SelectItem>
              <SelectItem value="7d">Last 7 days</SelectItem>
              <SelectItem value="30d">Last 30 days</SelectItem>
              <SelectItem value="all">All time</SelectItem>
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Patterns or Events */}
      {tab === 'patterns' ? (
        <PatternsList patterns={filteredPatterns} onSelect={setSelectedPattern} entities={entities} />
      ) : (
        <BlockedEventsTable
          events={sortedEvents}
          sortConfig={sortConfig}
          handleSort={handleSort}
          onSelect={setSelectedEvent}
          entities={entities}
        />
      )}

      {/* Pattern detail sheet */}
      <PatternDetailSheet
        pattern={selectedPattern}
        onClose={() => setSelectedPattern(null)}
        onSelectEvent={(ev) => { setSelectedPattern(null); setSelectedEvent(ev); }}
        allEvents={ACTIVITY_EVENTS}
        entities={entities}
      />

      {/* Event detail sheet */}
      <ActivityDetailSheet
        event={selectedEvent}
        onClose={() => setSelectedEvent(null)}
        allEvents={ACTIVITY_EVENTS}
      />
    </div>
  );
}

// ─── Patterns list ──────────────────────────────────────────────────────────

function PatternsList({ patterns, onSelect, entities }: { patterns: ViolationPattern[]; onSelect: (p: ViolationPattern) => void; entities: ReturnType<typeof useTrustEntities> }) {
  if (patterns.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Shield className="h-10 w-10 text-emerald-500/40 mb-3" />
        <p className="text-sm font-medium">No violation patterns detected</p>
        <p className="text-xs text-muted-foreground mt-1">
          This is good. Pattern detection runs continuously; findings will appear here when anomalies are seen.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {patterns.map(p => {
        const meta = PATTERN_TYPE_META[p.patternType] ?? { label: p.patternType, icon: Shield, color: TRUST_FALLBACK_STYLE.color };
        const sev = SEVERITY_STYLES[p.severity] ?? { ...TRUST_FALLBACK_STYLE, label: p.severity };
        const status = PATTERN_STATUS_STYLES[p.status] ?? { ...TRUST_FALLBACK_STYLE, label: p.status };
        const Icon = meta.icon;
        return (
          <Card
            key={p.id}
            className={cn('cursor-pointer hover:shadow-md transition-shadow', p.status === 'open' && 'border-l-4 border-l-red-500/60')}
            onClick={() => onSelect(p)}
          >
            <CardContent className="pt-4 pb-4">
              <div className="flex items-start gap-3">
                <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', meta.color)} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1">
                    <h3 className="text-sm font-semibold leading-snug">{p.title}</h3>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <Badge variant="outline" className={cn('text-[10px]', sev.color)}>{sev.label}</Badge>
                      <Badge variant="outline" className={cn('text-[10px]', status.color)}>{status.label}</Badge>
                    </div>
                  </div>
                  <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{p.description}</p>
                  <div className="flex items-center gap-3 flex-wrap text-[10px] text-muted-foreground">
                    <span className="inline-flex items-center gap-1">
                      <Activity className="h-3 w-3" />
                      {p.relatedEventIds.length} related event{p.relatedEventIds.length === 1 ? '' : 's'}
                    </span>
                    {p.affectedUserId && (
                      <span className="inline-flex items-center gap-1">
                        <User className="h-3 w-3" />
                        {entities.lookup.user(p.affectedUserId)?.name ?? `user:${shortId(p.affectedUserId)}`}
                      </span>
                    )}
                    {p.affectedAgentId && (
                      <span className="inline-flex items-center gap-1">
                        <Bot className="h-3 w-3" />
                        {entities.lookup.agent(p.affectedAgentId)?.name ?? `agent:${shortId(p.affectedAgentId)}`}
                      </span>
                    )}
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Detected {relativeTime(p.detectedAt)}
                    </span>
                    <Badge variant="outline" className="text-[9px]">
                      {meta.label}
                    </Badge>
                    {p.frameworkControls.slice(0, 3).map(fc => (
                      <code key={fc} className="text-[9px] bg-muted px-1.5 py-0 rounded">{fc}</code>
                    ))}
                    {p.frameworkControls.length > 3 && (
                      <span className="text-[9px]">+{p.frameworkControls.length - 3}</span>
                    )}
                  </div>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground shrink-0 mt-1" />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

// ─── Blocked events table ───────────────────────────────────────────────────

function BlockedEventsTable({
  events, sortConfig, handleSort, onSelect, entities,
}: {
  events: ActivityEvent[];
  sortConfig: any;
  handleSort: (k: string) => void;
  onSelect: (e: ActivityEvent) => void;
  entities: ReturnType<typeof useTrustEntities>;
}) {
  if (events.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <CheckCircle2 className="h-10 w-10 text-emerald-500/40 mb-3" />
        <p className="text-sm font-medium">No blocked events in selected window</p>
        <p className="text-xs text-muted-foreground mt-1">Platform is running without policy denials</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border overflow-x-auto">
      <div className="min-w-[1200px]">
        <div className="grid grid-cols-[90px_150px_110px_minmax(360px,2fr)_160px_140px_110px] gap-3 px-4 py-3 bg-muted/50 text-xs uppercase tracking-wider">
          <SortableHeader label="Seq" sortKey="sequenceNumber" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Time" sortKey="timestamp" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Category" sortKey="category" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Event" sortKey="summary" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Actor" sortKey="userId" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Outcome" sortKey="outcome" currentSort={sortConfig} onSort={handleSort} />
          <SortableHeader label="Severity" sortKey="severity" currentSort={sortConfig} onSort={handleSort} />
        </div>
        <div className="divide-y">
          {events.slice(0, 300).map(e => (
            <BlockedEventRow key={e.id} ev={e} onSelect={onSelect} entities={entities} />
          ))}
          {events.length > 300 && (
            <div className="p-3 text-center text-xs text-muted-foreground bg-muted/20">
              Showing first 300 of {events.length.toLocaleString()} events. Export for full results.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function BlockedEventRow({ ev, onSelect, entities }: { ev: ActivityEvent; onSelect: (e: ActivityEvent) => void; entities: ReturnType<typeof useTrustEntities> }) {
  // Real events can carry a category/outcome/severity value not present in the
  // style maps (the maps cover the curated mock vocabulary). Without a fallback,
  // the map lookup returns undefined and `.color` below crashes the ENTIRE
  // Blocked Events tab via the error boundary. Mirror activity.tsx's
  // FALLBACK_STYLE so an unknown value renders neutrally instead of crashing.
  const cat = CATEGORY_STYLES[ev.category] ?? { ...TRUST_FALLBACK_STYLE, label: ev.category };
  const oc = OUTCOME_STYLES[ev.outcome] ?? { ...TRUST_FALLBACK_STYLE, label: ev.outcome };
  const sev = SEVERITY_STYLES[ev.severity] ?? { ...TRUST_FALLBACK_STYLE, label: ev.severity };
  const user = entities.lookup.user(ev.userId);
  const agent = entities.lookup.agent(ev.agentId);
  const userFallback = !user && ev.userId ? `user:${shortId(ev.userId)}` : null;
  const agentFallback = !agent && ev.agentId ? `agent:${shortId(ev.agentId)}` : null;
  return (
    <div
      className="grid grid-cols-[90px_150px_110px_minmax(360px,2fr)_160px_140px_110px] gap-3 px-4 py-3 items-center text-xs hover:bg-muted/30 cursor-pointer bg-red-500/[0.02]"
      onClick={() => onSelect(ev)}
    >
      <span className="font-mono text-muted-foreground">#{ev.sequenceNumber}</span>
      <span className="text-muted-foreground" title={ev.timestamp}>{relativeTime(ev.timestamp)}</span>
      <Badge variant="outline" className={cn('text-[9px] w-fit', cat.color)}>{cat.label}</Badge>
      <p className="truncate">{ev.summary}</p>
      <div className="min-w-0 truncate text-[10px]">
        {(user || userFallback) && <span>{user?.name ?? userFallback}</span>}
        {(user || userFallback) && (agent || agentFallback) && <span className="mx-1">·</span>}
        {(agent || agentFallback) && <span className="text-muted-foreground">{agent?.name ?? agentFallback}</span>}
      </div>
      <Badge variant="outline" className={cn('text-[9px] w-fit', oc.color)}>{oc.label}</Badge>
      <Badge variant="outline" className={cn('text-[9px] w-fit', sev.color)}>{sev.label}</Badge>
    </div>
  );
}

// ─── Pattern detail sheet ───────────────────────────────────────────────────

function PatternDetailSheet({
  pattern, onClose, onSelectEvent, allEvents, entities,
}: {
  pattern: ViolationPattern | null;
  onClose: () => void;
  onSelectEvent: (e: ActivityEvent) => void;
  allEvents: ActivityEvent[];
  entities: ReturnType<typeof useTrustEntities>;
}) {
  const [status, setStatus] = useState<ViolationPattern['status']>(pattern?.status ?? 'open');
  const [note, setNote] = useState('');

  if (!pattern) return null;

  const meta = PATTERN_TYPE_META[pattern.patternType] ?? { label: pattern.patternType, icon: Shield, color: TRUST_FALLBACK_STYLE.color };
  const sev = SEVERITY_STYLES[pattern.severity] ?? { ...TRUST_FALLBACK_STYLE, label: pattern.severity };
  const statusStyle = PATTERN_STATUS_STYLES[status] ?? { ...TRUST_FALLBACK_STYLE, label: status };
  const user = entities.lookup.user(pattern.affectedUserId);
  const agent = entities.lookup.agent(pattern.affectedAgentId);

  const relatedEvents = allEvents.filter(e => pattern.relatedEventIds.includes(e.id))
    .sort((a, b) => a.sequenceNumber - b.sequenceNumber);

  // Platform audit 2026-06-10/11: was toast-only + local setState (pretended to
  // persist on a compliance surface). Now calls the real, mounted endpoint
  // PUT /governance/incidents/:id/resolve with the EXACT contract it requires:
  //   status ∈ {resolved, false_positive}, resolutionAction (enum), resolutionNotes (required).
  // (The end-to-end proof caught that the old wiring sent the wrong shape →
  // 422.) Only REAL incidents (UUID ids) can be resolved; DERIVED/demo patterns
  // (inferred from activity logs) have no incident record, so the action area is
  // replaced with an honest notice instead of a button that 404s.
  const isResolvable = !!pattern?.id && UUID_RE.test(pattern.id);
  const [resolutionAction, setResolutionAction] = useState<
    'dismissed' | 'user_warned' | 'user_blocked' | 'policy_updated' | 'escalated'
  >('dismissed');
  const canSubmitResolve = isResolvable && note.trim().length > 0;

  const handleResolve = async (newStatus: 'resolved' | 'false_positive') => {
    if (!canSubmitResolve) return;
    try {
      await apiClient.put(`/governance/incidents/${pattern!.id}/resolve`, {
        status: newStatus,
        resolutionAction,
        resolutionNotes: note.trim(),
      });
      toast.success(`Incident marked as ${(PATTERN_STATUS_STYLES[newStatus]?.label ?? newStatus).toLowerCase()}`);
      setStatus(newStatus);
    } catch (err) {
      toast.error('Could not update incident', {
        description: err instanceof Error ? err.message : 'The request failed.',
      });
    }
  };

  const Icon = meta.icon;

  return (
    <Sheet open={!!pattern} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="sm:max-w-2xl w-full overflow-y-auto">
        <SheetHeader>
          <div className="flex items-start gap-3 mb-2">
            <Icon className={cn('h-5 w-5 shrink-0 mt-0.5', meta.color)} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="outline" className={cn('text-[10px]', sev.color)}>{sev.label}</Badge>
                <Badge variant="outline" className={cn('text-[10px]', statusStyle.color)}>
                  {statusStyle.label}
                </Badge>
                <Badge variant="outline" className="text-[10px]">{meta.label}</Badge>
              </div>
              <SheetTitle className="text-sm leading-snug">{pattern.title}</SheetTitle>
              <SheetDescription className="text-xs mt-1">
                Detected {relativeTime(pattern.detectedAt)}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="space-y-5 mt-4">
          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Description</p>
            <p className="text-xs">{pattern.description}</p>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Affected</p>
            <div className="rounded-md border p-3 space-y-2 text-xs">
              {user && (
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="font-medium">{user.name}</p>
                    <p className="text-[10px] text-muted-foreground">
                      {user.email} · {user.role}
                    </p>
                  </div>
                </div>
              )}
              {agent && (
                <div className="flex items-center gap-2">
                  <Bot className={cn('h-4 w-4', `text-${agent.iconColor}-600 dark:text-${agent.iconColor}-400`)} />
                  <div>
                    <p className="font-medium">{agent.name}</p>
                    <p className="text-[10px] text-muted-foreground capitalize">{agent.type}</p>
                  </div>
                </div>
              )}
              {!user && !agent && (
                <p className="text-[10px] text-muted-foreground italic">No specific user or agent (system-wide)</p>
              )}
            </div>
          </section>

          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Recommended Action</p>
            <div className="rounded-md border bg-amber-500/5 border-amber-500/20 p-3">
              <p className="text-xs">{pattern.recommendedAction}</p>
            </div>
          </section>

          {pattern.frameworkControls.length > 0 && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Framework Controls</p>
              <div className="flex flex-wrap gap-1.5">
                {pattern.frameworkControls.map(fc => (
                  <code key={fc} className="text-[10px] bg-muted px-2 py-0.5 rounded border">{fc}</code>
                ))}
              </div>
            </section>
          )}

          <section>
            <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
              Related Events ({relatedEvents.length})
            </p>
            <div className="rounded-md border divide-y">
              {relatedEvents.slice(0, 20).map(e => (
                <div
                  key={e.id}
                  className="flex items-center gap-2 px-3 py-2 text-xs hover:bg-muted/30 cursor-pointer"
                  onClick={() => onSelectEvent(e)}
                >
                  <span className="font-mono text-[9px] text-muted-foreground shrink-0">#{e.sequenceNumber}</span>
                  <span className="flex-1 min-w-0 truncate">{e.summary}</span>
                  <Badge variant="outline" className={cn('text-[9px] shrink-0', (OUTCOME_STYLES[e.outcome] ?? TRUST_FALLBACK_STYLE).color)}>
                    {(OUTCOME_STYLES[e.outcome] ?? { ...TRUST_FALLBACK_STYLE, label: e.outcome }).label}
                  </Badge>
                  <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
                </div>
              ))}
              {relatedEvents.length > 20 && (
                <div className="p-2 text-center text-[10px] text-muted-foreground bg-muted/20">
                  + {relatedEvents.length - 20} more events
                </div>
              )}
            </div>
          </section>

          {pattern.status === 'resolved' && pattern.resolvedAt && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Resolution</p>
              <div className="rounded-md border bg-emerald-500/5 border-emerald-500/20 p-3 space-y-1 text-xs">
                <p>Resolved {relativeTime(pattern.resolvedAt)} by{' '}
                  <span className="font-medium">{entities.lookup.user(pattern.resolvedBy)?.name ?? (pattern.resolvedBy ? `user:${shortId(pattern.resolvedBy)}` : '—')}</span>
                </p>
                {pattern.resolvedNote && <p className="text-muted-foreground mt-1.5">{pattern.resolvedNote}</p>}
              </div>
            </section>
          )}

          {/* Actions */}
          {status !== 'resolved' && status !== 'false_positive' && (
            <section>
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">Resolve Incident</p>
              {!isResolvable ? (
                <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-xs text-amber-700 dark:text-amber-300">
                  This pattern is derived from activity logs and is not a standalone
                  incident record, so it can’t be resolved here. Investigate the
                  related events; a resolution applies to persisted incidents only.
                </div>
              ) : (
                <div className="rounded-md border p-3 space-y-2">
                  <select
                    value={resolutionAction}
                    onChange={(e) => setResolutionAction(e.target.value as typeof resolutionAction)}
                    className="w-full text-xs rounded border border-border bg-background px-2 py-1.5"
                  >
                    <option value="dismissed">Action: Dismissed</option>
                    <option value="user_warned">Action: User warned</option>
                    <option value="user_blocked">Action: User blocked</option>
                    <option value="policy_updated">Action: Policy updated</option>
                    <option value="escalated">Action: Escalated</option>
                  </select>
                  <textarea
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    placeholder="Resolution note (required)…"
                    className="w-full text-xs rounded border border-border bg-background px-2 py-1.5 min-h-[60px]"
                  />
                  <div className="flex gap-2 flex-wrap items-center">
                    <Button size="sm" className="h-7 text-xs" disabled={!canSubmitResolve} onClick={() => handleResolve('resolved')}>
                      <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Resolved
                    </Button>
                    <Button size="sm" variant="outline" className="h-7 text-xs" disabled={!canSubmitResolve} onClick={() => handleResolve('false_positive')}>
                      <XCircle className="h-3 w-3 mr-1" /> False Positive
                    </Button>
                    {note.trim().length === 0 && (
                      <span className="text-[10px] text-muted-foreground">A resolution note is required.</span>
                    )}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function relativeTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
}
